/**
 * Alta de una orden de etiquetas.
 *
 * Los límites son los de `LabelOrderIn`: hasta 50.000.000 de etiquetas por
 * orden y hasta 1.000.000 por rollo. Se validan acá para no mandar un 422 de
 * ida y vuelta, y se muestra en vivo **cuántos rollos** va a producir la
 * combinación — que es el dato que el depósito necesita antes de firmar la
 * orden, y el que nadie calcula de cabeza con 107.941 unidades de a 27.000.
 *
 * `key_version` no está en el formulario: es la versión de la clave HMAC con
 * la que se firman los códigos de esta orden y rotarla es una operación de
 * plataforma, no una decisión de quien compra etiquetas. La API la deja en 1.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { useKeysetQuery } from '@/api/hooks';
import { productKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { SelectField, TextAreaField, TextField } from '../products/ui/fields';

type Product = Schemas['ProductOut'];

const MAX_QUANTITY = 50_000_000;
const MAX_UNITS_PER_ROLL = 1_000_000;
const PRODUCT_OPTIONS = 200;

export interface LabelOrderDraft {
  order_code: string;
  supplier: string;
  quantity: number;
  product_id: number | null;
  units_per_roll: number;
  notes: string | null;
}

export interface LabelOrderFormProps {
  onSubmit: (draft: LabelOrderDraft) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error: unknown;
}

export function LabelOrderForm({
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: LabelOrderFormProps): ReactNode {
  const { t, fmt } = useI18n();
  const [orderCode, setOrderCode] = useState('');
  const [supplier, setSupplier] = useState('');
  const [quantity, setQuantity] = useState('100000');
  const [unitsPerRoll, setUnitsPerRoll] = useState('10000');
  const [productId, setProductId] = useState('');
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);

  const products = useKeysetQuery<Product>(
    productKeys.list({ limit: PRODUCT_OPTIONS }),
    '/api/v1/products',
    undefined,
    { limit: PRODUCT_OPTIONS },
  );

  const quantityValue = Number(quantity);
  const perRollValue = Number(unitsPerRoll);
  const quantityOk = Number.isInteger(quantityValue) && quantityValue >= 1 && quantityValue <= MAX_QUANTITY;
  const perRollOk =
    Number.isInteger(perRollValue) && perRollValue >= 1 && perRollValue <= MAX_UNITS_PER_ROLL;
  const rollCount = quantityOk && perRollOk ? Math.ceil(quantityValue / perRollValue) : null;

  const codeError = touched && orderCode.trim() === '' ? t('form.required') : undefined;
  const supplierError = touched && supplier.trim() === '' ? t('form.required') : undefined;
  const quantityError =
    touched && !quantityOk ? t('labels.order.field.quantity.invalid', { max: MAX_QUANTITY }) : undefined;
  const perRollError =
    touched && !perRollOk
      ? t('labels.order.field.unitsPerRoll.invalid', { max: MAX_UNITS_PER_ROLL })
      : undefined;

  const canSubmit = orderCode.trim() !== '' && supplier.trim() !== '' && quantityOk && perRollOk;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({
      order_code: orderCode.trim(),
      supplier: supplier.trim(),
      quantity: quantityValue,
      product_id: productId === '' ? null : Number(productId),
      units_per_roll: perRollValue,
      notes: notes.trim() === '' ? null : notes.trim(),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <TextField
        label={t('labels.order.field.code')}
        hint={t('labels.order.field.code.hint')}
        value={orderCode}
        onChange={setOrderCode}
        error={codeError}
        required
        mono
        maxLength={64}
        placeholder="OC-2026-001"
      />

      <TextField
        label={t('labels.order.field.supplier')}
        value={supplier}
        onChange={setSupplier}
        error={supplierError}
        required
        maxLength={120}
        placeholder="Etiquetas del Sur S.A."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label={t('labels.order.field.quantity')}
          type="number"
          min={1}
          max={MAX_QUANTITY}
          value={quantity}
          onChange={setQuantity}
          error={quantityError}
          required
        />
        <TextField
          label={t('labels.order.field.unitsPerRoll')}
          type="number"
          min={1}
          max={MAX_UNITS_PER_ROLL}
          value={unitsPerRoll}
          onChange={setUnitsPerRoll}
          error={perRollError}
          required
        />
      </div>

      <p className="rounded border border-border bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
        {rollCount === null
          ? t('labels.order.rollPreview.unknown')
          : t('labels.order.rollPreview', {
              rolls: fmt.number(rollCount),
              units: fmt.number(quantityValue),
            })}
      </p>

      <SelectField
        label={t('labels.order.field.product')}
        hint={t('labels.order.field.product.hint')}
        value={productId}
        onChange={setProductId}
        options={[
          { value: '', label: t('labels.order.field.product.none') },
          ...products.items.map((product) => ({
            value: String(product.id),
            label: `${product.sku} — ${product.name}`,
          })),
        ]}
      />

      <TextAreaField
        label={t('labels.order.field.notes')}
        value={notes}
        onChange={setNotes}
        rows={2}
        maxLength={500}
      />

      {Boolean(error) && <ErrorState error={error} />}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : t('labels.orders.create')}
        </Button>
      </div>
    </form>
  );
}
